import type {
  FormAudioPrompt,
  FormImagePrompt,
  FormSubtitlePrompt,
  ImagePrompt,
  OneClickMvScene,
  OneClickMvSubtitleLanguage,
  OneClickMvVersion,
  TaskInfo,
  TaskInputPrompts,
  ToolFormParams,
} from '@/app/types';
import { getOneClickMvTaskScene } from './one-click-mv-task-metadata';

type OneClickMvApiMode = NonNullable<NonNullable<TaskInfo['settings']>['music_video']>['mode'];

const ONE_CLICK_MV_SCENE_FROM_API_MODE: Partial<Record<NonNullable<OneClickMvApiMode>, OneClickMvScene>> = {
  sing: 'sing',
  sing_perform: 'sing_act',
  dance: 'dance',
  perform: 'performance',
};

type OneClickMvRecreateTask = {
  id?: string;
  input: TaskInfo['input'];
  settings?: TaskInfo['settings'];
};

const hasOneClickMvV2Input = (task: OneClickMvRecreateTask): boolean => {
  if (task.input?.lip_sync === true) return true;
  return (task.input?.prompts || []).some((prompt) => prompt.type === 'image' && prompt.lip_sync?.enable === true);
};

const getSelectedImageRange = (prompt: TaskInputPrompts): ImagePrompt['selectedImageRange'] => {
  if (!prompt.selected_region) return undefined;

  return [
    prompt.selected_region.top_left.x,
    prompt.selected_region.top_left.y,
    prompt.selected_region.bottom_right.x,
    prompt.selected_region.bottom_right.y,
  ];
};

const createOneClickMvImagePrompt = (prompt: TaskInputPrompts): FormImagePrompt | null => {
  if (prompt.type === 'material' && prompt.material?.id) {
    const content = prompt.content || prompt.material.contents?.[0] || '';

    return {
      id: prompt.material.id,
      status: 'done',
      material: {
        id: prompt.material.id,
        version: prompt.material.version || '',
        name: prompt.name,
        share_id: prompt.material.share_id,
      },
      prompt: content
        ? {
            sourceImage: content,
            sourceImagePreview: content,
            selectedImage: content,
            selectedImagePreview: content,
          }
        : null,
    };
  }

  if (prompt.type !== 'image' || !prompt.content) return null;

  const sourceImage = prompt.src_imgs?.[0] || prompt.content;
  const referenceCreationId = prompt.reference?.creation_id;

  return {
    id: referenceCreationId || prompt.content,
    status: 'done',
    prompt: {
      sourceImage,
      sourceImagePreview: sourceImage,
      selectedImage: prompt.content,
      selectedImagePreview: prompt.content,
      selectedImageRange: getSelectedImageRange(prompt),
    },
    reference: referenceCreationId
      ? {
          creation_id: referenceCreationId,
        }
      : undefined,
  };
};

const createOneClickMvAudioPrompt = (prompt: TaskInputPrompts, duration?: number): FormAudioPrompt | null => {
  if (prompt.type !== 'audio' || !prompt.content) return null;

  return {
    id: prompt.content,
    status: 'done',
    prompt: {
      src: prompt.content,
      preview: prompt.content,
      name: prompt.name,
      duration,
    },
  };
};

const createOneClickMvSubtitlePrompt = (prompt: TaskInputPrompts): FormSubtitlePrompt | null => {
  if (prompt.type !== 'subtitle' || !prompt.content) return null;

  return {
    id: prompt.content,
    status: 'done',
    prompt: {
      src: prompt.content,
      fileName: prompt.name,
    },
  };
};

const isLipSyncRolePrompt = (prompt: TaskInputPrompts): boolean => {
  return prompt.type === 'image' && prompt.lip_sync?.enable === true;
};

const getOneClickMvVersion = (mode: OneClickMvApiMode): OneClickMvVersion => {
  return mode && mode !== 'unspecified' ? 'v2' : 'v1';
};

const getOneClickMvScene = (mode: OneClickMvApiMode, storedScene?: OneClickMvScene): OneClickMvScene => {
  if (!mode || mode === 'unspecified') return storedScene ?? 'sing';
  return ONE_CLICK_MV_SCENE_FROM_API_MODE[mode] ?? storedScene ?? 'sing';
};

const ONE_CLICK_MV_SUBTITLE_LANGUAGES = new Set<OneClickMvSubtitleLanguage>([
  'auto',
  'en',
  'zh',
  'ja',
  'ko',
  'vi',
  'th',
  'id',
  'ms',
  'tl',
  'hi',
  'ar',
  'fr',
  'de',
  'es',
  'pt',
  'ru',
  'it',
  'nl',
  'sv',
  'da',
  'fi',
  'no',
  'el',
  'pl',
  'cs',
  'hu',
  'ro',
  'bg',
  'hr',
  'sk',
]);

const getOneClickMvSubtitleLanguage = (value?: string): OneClickMvSubtitleLanguage | undefined =>
  value && ONE_CLICK_MV_SUBTITLE_LANGUAGES.has(value as OneClickMvSubtitleLanguage)
    ? (value as OneClickMvSubtitleLanguage)
    : undefined;

export const buildOneClickMvRecreatePatch = ({
  task,
  baseSettings,
}: {
  task: OneClickMvRecreateTask;
  baseSettings?: TaskInfo['settings'];
}): Partial<ToolFormParams> => {
  const prompts = task.input?.prompts || [];
  const musicVideoSettings = task.settings?.music_video;
  const storedScene = getOneClickMvTaskScene(task.id);
  const version =
    getOneClickMvVersion(musicVideoSettings?.mode) === 'v2' || hasOneClickMvV2Input(task) || !!storedScene
      ? 'v2'
      : 'v1';
  const scene = getOneClickMvScene(musicVideoSettings?.mode, storedScene);
  const imagePrompts = prompts
    .filter((prompt) => (prompt.type === 'image' || prompt.type === 'material') && !isLipSyncRolePrompt(prompt))
    .map(createOneClickMvImagePrompt)
    .filter((prompt): prompt is FormImagePrompt => prompt !== null);
  const referenceImagePrompts =
    version === 'v2'
      ? prompts
          .filter(isLipSyncRolePrompt)
          .sort((a, b) => (a.lip_sync?.index ?? 0) - (b.lip_sync?.index ?? 0))
          .map(createOneClickMvImagePrompt)
          .filter((prompt): prompt is FormImagePrompt => prompt !== null)
      : [];
  const audioPrompts = prompts
    .map((prompt) => createOneClickMvAudioPrompt(prompt, task.settings?.duration))
    .filter((prompt): prompt is FormAudioPrompt => prompt !== null)
    .slice(0, 1);
  const subtitlePrompts =
    version === 'v1'
      ? prompts
          .map(createOneClickMvSubtitlePrompt)
          .filter((prompt): prompt is FormSubtitlePrompt => prompt !== null)
          .slice(0, 1)
      : [];
  const textPrompt = prompts.find((prompt) => prompt.type === 'text')?.content || '';

  return {
    imagePrompts,
    referenceImagePrompts,
    audioPrompts,
    subtitlePrompts,
    videoPrompts: [],
    textPrompt,
    oneClickMvVersion: version,
    oneClickMvScene: scene,
    oneClickMvSubtitleEnabled: version === 'v2' ? (musicVideoSettings?.subtitle_enable ?? false) : false,
    oneClickMvSubtitleLanguage:
      version === 'v2' ? getOneClickMvSubtitleLanguage(musicVideoSettings?.subtitle_language) : undefined,
    settings: {
      ...(baseSettings || {}),
      ...(task.settings || {}),
    },
  };
};
